import React, { useEffect, useSyncExternalStore } from 'react';

/**
 * Registry of views that currently hold unsaved changes (TAK-15).
 *
 * Editing views report their dirty state (SaveButton via its `guardId` prop,
 * or useUnsavedChangesGuard for views with a custom save UI). The registry is
 * read by the two navigation guards:
 *  - GuardedLink in the Navbar confirms in-app navigation,
 *  - UnsavedChangesGuard (rendered once in App) covers refresh / tab close
 *    through beforeunload.
 */

const dirtyViews = new Set();
const listeners = new Set();

export function reportUnsavedChanges(id, dirty) {
    const before = dirtyViews.size > 0;
    if (dirty) {
        dirtyViews.add(id);
    } else {
        dirtyViews.delete(id);
    }
    if ((dirtyViews.size > 0) !== before) listeners.forEach(fn => fn());
}

export function hasUnsavedChanges() {
    return dirtyViews.size > 0;
}

export function subscribeUnsavedChanges(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
}

// Shared leave-confirmation for in-app navigation. Returns false when the
// navigation must be cancelled.
export function confirmLeaveUnsaved(t) {
    if (!hasUnsavedChanges()) return true;
    return window.confirm(t('unsaved-warning'));
}

// For views with a custom save UI (no SaveButton): report the dirty state and
// clear the registry entry when the view unmounts.
export function useUnsavedChangesGuard(id, dirty) {
    useEffect(() => {
        reportUnsavedChanges(id, dirty);
        return () => reportUnsavedChanges(id, false);
    }, [id, dirty]);
}

// Rendered once in App: triggers the browser's leave dialog on refresh /
// tab close / back button while any view is dirty.
export function UnsavedChangesGuard() {
    const dirty = useSyncExternalStore(subscribeUnsavedChanges, hasUnsavedChanges);

    useEffect(() => {
        if (!dirty) return;
        const handler = (e) => {
            e.preventDefault();
            e.returnValue = ''; // required for Chrome to show the dialog
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [dirty]);

    return null;
}
